// 승인 카드 diff 뷰용 블록 정렬.
//
// base(turn 시작 blocks) 와 after(커밋될 전체 blocks)를 content-hash 기준 LCS 로 정렬해
// 변경된 블록만 분류한다. edit_blocks 의 ops(순차 적용으로 sequence 가 밀림)에 의존하지 않아
// 다중 op 커밋에서도 정확하다. 결과는 "무엇이 바뀌었는지"만 담는다(unchanged 는 제외).

import type { TaskEditorBlock } from "./taskEditorUtils";
import { hashBlock } from "./taskEditorDiff";

export type BlockDiffRow =
  | {
      kind: "modified";
      before: TaskEditorBlock;
      after: TaskEditorBlock;
      beforeIndex: number; // 0-based
      afterIndex: number;
    }
  | { kind: "added"; block: TaskEditorBlock; afterIndex: number }
  | { kind: "removed"; block: TaskEditorBlock; beforeIndex: number }
  | {
      kind: "moved";
      block: TaskEditorBlock;
      beforeIndex: number;
      afterIndex: number;
    };

type Raw =
  | { t: "same"; i: number; j: number }
  | { t: "del"; i: number }
  | { t: "add"; j: number };

// computeLineDiff 와 동일한 LCS DP — 여기선 라인 대신 블록 해시 배열을 정렬한다.
function lcsWalk(a: string[], b: string[]): Raw[] {
  const n = a.length;
  const m = b.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () =>
    new Array(m + 1).fill(0)
  );
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      if (a[i] === b[j]) dp[i][j] = dp[i + 1][j + 1] + 1;
      else dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const raw: Raw[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      raw.push({ t: "same", i, j });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      raw.push({ t: "del", i });
      i++;
    } else {
      raw.push({ t: "add", j });
      j++;
    }
  }
  while (i < n) raw.push({ t: "del", i: i++ });
  while (j < m) raw.push({ t: "add", j: j++ });
  return raw;
}

export function alignBlocks(
  base: TaskEditorBlock[],
  after: TaskEditorBlock[]
): BlockDiffRow[] {
  const ha = base.map(hashBlock);
  const hb = after.map(hashBlock);
  const raw = lcsWalk(ha, hb);

  // 이동(move) 감지: del 과 add 가 같은 해시면 내용 그대로 위치만 바뀐 것 → moved 로 짝짓는다.
  // 같은 해시가 여럿이면 순서대로 큐로 짝짓는다.
  const addQueue = new Map<string, number[]>();
  for (const e of raw) {
    if (e.t === "add") {
      const list = addQueue.get(hb[e.j]) ?? [];
      list.push(e.j);
      addQueue.set(hb[e.j], list);
    }
  }
  const movedBefore = new Map<number, number>(); // beforeIndex -> afterIndex
  const movedAfter = new Set<number>();
  for (const e of raw) {
    if (e.t !== "del") continue;
    const candidates = addQueue.get(ha[e.i]);
    if (candidates && candidates.length > 0) {
      const afterIndex = candidates.shift() as number;
      movedBefore.set(e.i, afterIndex);
      movedAfter.add(afterIndex);
    }
  }

  const rows: BlockDiffRow[] = [];
  for (let k = 0; k < raw.length; k++) {
    const e = raw[k];
    if (e.t === "same") continue; // 변경 없음 — diff 에 안 보인다.
    if (e.t === "del") {
      const movedTo = movedBefore.get(e.i);
      if (movedTo !== undefined) {
        rows.push({
          kind: "moved",
          block: base[e.i],
          beforeIndex: e.i,
          afterIndex: movedTo,
        });
        continue;
      }
      // 바로 뒤가 (이동이 아닌) add 면 한 블록 수정으로 묶는다 (before→after).
      const next = raw[k + 1];
      if (next && next.t === "add" && !movedAfter.has(next.j)) {
        rows.push({
          kind: "modified",
          before: base[e.i],
          after: after[next.j],
          beforeIndex: e.i,
          afterIndex: next.j,
        });
        k++; // add 소비
        continue;
      }
      rows.push({ kind: "removed", block: base[e.i], beforeIndex: e.i });
      continue;
    }
    // add
    if (movedAfter.has(e.j)) continue; // 대응 del 에서 moved 로 이미 발행됨.
    rows.push({ kind: "added", block: after[e.j], afterIndex: e.j });
  }
  return rows;
}
